import { isBrowser } from './detection';
import { scrollToElementIntoView } from './scroll';

/**
 * Checks if a URL is a hash anchor on the current page.
 * Returns the element ID if it's a same-page anchor, undefined otherwise.
 *
 * @param href - The URL to check
 * @returns The element ID if it's a same-page anchor, undefined otherwise
 *
 * @example
 * getHashAnchor('#customer-support') // 'customer-support'
 * getHashAnchor('customer-support') // 'customer-support'
 * getHashAnchor('/page#section') // undefined (different page)
 * getHashAnchor('https://example.com#section') // undefined (external)
 */
function getHashAnchor(href: string): string | undefined {
  if (!isBrowser()) return undefined;

  // Plain hash: #customer-support
  if (href.startsWith('#')) {
    return href.slice(1);
  }

  // Plain ID without hash: customer-support (no slashes, no protocol)
  const hasPath = href.includes('/');
  const hasProtocol = href.startsWith('http://') || href.startsWith('https://');

  if (!hasPath && !hasProtocol) {
    return href;
  }

  // URL with hash on the same page: /current-path#section
  try {
    const url = new URL(href, window.location.origin);
    const isSamePage = url.origin === window.location.origin && url.pathname === window.location.pathname;

    if (isSamePage && url.hash) {
      return url.hash.slice(1);
    }
  } catch {
    // Invalid URL, treat as regular navigation
    return undefined;
  }

  return undefined;
}

/**
 * Navigate to a URL, either in the same tab or a new tab.
 *
 * Handles three types of navigation:
 * 1. External links → Opens in new tab if target='_blank'
 * 2. Hash anchors → Smooth scrolls to element on current page
 * 3. Regular links → Standard navigation
 *
 * For hash links (#customer-support), uses smooth scrolling instead of browser default.
 * This prevents layout shift issues when images/content load after the scroll.
 *
 * @param href - The URL to navigate to (full URL, path, hash, or element ID)
 * @param target - The target attribute (e.g., '_blank' for new tab)
 * @param rel - The rel attribute (e.g., 'noopener noreferrer')
 *
 * @example
 * navigateTo('https://example.com', '_blank'); // New tab
 * navigateTo('/products'); // Same tab navigation
 * navigateTo('#customer-support'); // Smooth scroll to element
 * navigateTo('customer-support'); // Same as above (ID without #)
 */
export function navigateTo(href: string, target?: string, rel?: string): void {
  if (!isBrowser()) return;

  // Handle new tab navigation
  if (target === '_blank') {
    window.open(href, target, rel);

    return;
  }

  // Check if this is a hash anchor on the current page
  const elementId = getHashAnchor(href);

  if (elementId) {
    // Use smooth scroll for hash links to prevent layout shift issues
    scrollToElementIntoView(elementId);

    return;
  }

  // Default: standard page navigation
  window.location.assign(href);
}
